import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import { Box } from '@mui/material';

export interface InfiniteScrollPagingHandlerProps {
  children: ReactNode;
  hasPrevious?: boolean;
  hasMore?: boolean;
  isInitialLoading?: boolean;
  isLoading?: boolean;
  isLoadingPrevious?: boolean;
  itemCount?: number;
  loadPrevious?: () => void | Promise<unknown>;
  loadMore?: () => void | Promise<unknown>;
  loader?: ReactNode;
  previousLoader?: ReactNode;
  initialLoader?: ReactNode;
  scrollContainerRef?: RefObject<Element | null>;
  rootMargin?: string;
}

interface PrependScrollSnapshot {
  container: Element;
  itemCount: number;
  scrollHeight: number;
  scrollTop: number;
}

const findScrollContainer = (element: Element | null, configuredContainer?: Element | null) => {
  if (configuredContainer) return configuredContainer;

  let parent = element?.parentElement ?? null;
  while (parent) {
    const overflowY = window.getComputedStyle(parent).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') return parent;
    parent = parent.parentElement;
  }

  return document.scrollingElement ?? document.documentElement;
};

const InfiniteScrollPagingHandler = ({
  children,
  hasPrevious = false,
  hasMore = false,
  isInitialLoading = false,
  isLoading = false,
  isLoadingPrevious = false,
  itemCount = 0,
  loadPrevious,
  loadMore,
  loader,
  previousLoader = loader,
  initialLoader = loader,
  scrollContainerRef,
  rootMargin = '0px',
}: InfiniteScrollPagingHandlerProps) => {
  const previousSentinelRef = useRef<HTMLDivElement>(null);
  const nextSentinelRef = useRef<HTMLDivElement>(null);
  const pagingStateRef = useRef<{ requestPending: boolean; prependSnapshot: PrependScrollSnapshot | null }>({
    requestPending: false,
    prependSnapshot: null,
  });
  const [pendingDirection, setPendingDirection] = useState<'previous' | 'next' | null>(null);
  const isPreviousPageLoading = isLoadingPrevious || pendingDirection === 'previous';
  const isNextPageLoading = isLoading || pendingDirection === 'next';
  const isPageLoading = isPreviousPageLoading || isNextPageLoading;
  const showPreviousSentinel = Boolean(!isInitialLoading && hasPrevious && loadPrevious);
  const showNextSentinel = Boolean(!isInitialLoading && hasMore && loadMore);
  const canLoadPrevious = showPreviousSentinel && !isPageLoading;
  const canLoadMore = showNextSentinel && !isPageLoading;

  useLayoutEffect(() => {
    const snapshot = pagingStateRef.current.prependSnapshot;
    if (!snapshot) return;

    if (itemCount !== snapshot.itemCount) {
      snapshot.container.scrollTop = snapshot.scrollTop + snapshot.container.scrollHeight - snapshot.scrollHeight;
      pagingStateRef.current.prependSnapshot = null;
    } else if (!isPreviousPageLoading) {
      pagingStateRef.current.prependSnapshot = null;
    }
  }, [isPreviousPageLoading, itemCount]);

  const requestPage = useCallback(
    (direction: 'previous' | 'next', loadPage?: () => void | Promise<unknown>) => {
      if (pagingStateRef.current.requestPending || !loadPage) return;

      if (direction === 'previous') {
        const container = findScrollContainer(previousSentinelRef.current, scrollContainerRef?.current);
        if (container) {
          pagingStateRef.current.prependSnapshot = {
            container,
            itemCount,
            scrollHeight: container.scrollHeight,
            scrollTop: container.scrollTop,
          };
        }
      }

      pagingStateRef.current.requestPending = true;
      setPendingDirection(direction);
      const completeRequest = () => {
        pagingStateRef.current.requestPending = false;
        setPendingDirection(null);
      };
      try {
        const request = loadPage();
        if (!request) {
          completeRequest();
          return;
        }
        Promise.resolve(request)
          .catch(() => undefined)
          .finally(completeRequest);
      } catch {
        completeRequest();
      }
    },
    [itemCount, scrollContainerRef],
  );

  useEffect(() => {
    const previousSentinel = previousSentinelRef.current;
    const nextSentinel = nextSentinelRef.current;
    if ((!canLoadPrevious || !previousSentinel) && (!canLoadMore || !nextSentinel)) return;

    const requestPreviousPage = () => requestPage('previous', loadPrevious);
    const requestNextPage = () => requestPage('next', loadMore);

    const scrollContainer = scrollContainerRef?.current ?? null;
    if (typeof IntersectionObserver !== 'undefined') {
      const observer = new IntersectionObserver(
        entries => {
          if (entries.some(entry => entry.isIntersecting && entry.target === previousSentinel)) requestPreviousPage();
          if (entries.some(entry => entry.isIntersecting && entry.target === nextSentinel)) requestNextPage();
        },
        { root: scrollContainer, rootMargin },
      );
      if (canLoadPrevious && previousSentinel) observer.observe(previousSentinel);
      if (canLoadMore && nextSentinel) observer.observe(nextSentinel);
      return () => observer.disconnect();
    }

    const isSentinelVisible = (sentinel: Element) => {
      const sentinelRect = sentinel.getBoundingClientRect();
      const rootRect = scrollContainer?.getBoundingClientRect();
      const topBoundary = rootRect?.top ?? 0;
      const bottomBoundary = rootRect?.bottom ?? window.innerHeight;
      return sentinelRect.top <= bottomBoundary && sentinelRect.bottom >= topBoundary;
    };
    const checkSentinels = () => {
      if (canLoadPrevious && previousSentinel && isSentinelVisible(previousSentinel)) requestPreviousPage();
      if (canLoadMore && nextSentinel && isSentinelVisible(nextSentinel)) requestNextPage();
    };
    const scrollTarget = scrollContainer ?? window;
    scrollTarget.addEventListener('scroll', checkSentinels, { passive: true });
    window.addEventListener('resize', checkSentinels, { passive: true });
    checkSentinels();

    return () => {
      scrollTarget.removeEventListener('scroll', checkSentinels);
      window.removeEventListener('resize', checkSentinels);
    };
  }, [canLoadMore, canLoadPrevious, itemCount, loadMore, loadPrevious, requestPage, rootMargin, scrollContainerRef]);

  if (isInitialLoading) return <>{initialLoader}</>;

  return (
    <>
      {showPreviousSentinel && (
        <Box
          ref={previousSentinelRef}
          data-testid="infinite-scroll-previous-sentinel"
          aria-hidden="true"
          sx={{ height: 1 }}
        />
      )}
      {isPreviousPageLoading && (
        <Box sx={{ position: 'relative', height: 0, zIndex: 1 }}>
          <Box sx={{ position: 'absolute', right: 0, bottom: 0, left: 0 }}>{previousLoader}</Box>
        </Box>
      )}
      <Box sx={{ overflowAnchor: 'none' }}>{children}</Box>
      {isNextPageLoading && loader}
      {showNextSentinel && (
        <Box ref={nextSentinelRef} data-testid="infinite-scroll-next-sentinel" aria-hidden="true" sx={{ height: 1 }} />
      )}
    </>
  );
};

export default InfiniteScrollPagingHandler;
